import { Card, CardContent, CardFooter, CardHeader, CardImage, CardTitle } from '@vfz/ui';
import { Wrapper } from 'components/BaseLayout/components/Wrapper';
import { VZButton } from 'components/Button';
import { ChildPage } from 'components/ChildPage';
import { Col, Row } from 'components/Grid';
import { Loader } from 'components/Loader';
import { PageNotFound } from 'components/PageNotFound';
import { useGuruContext } from 'context/guru/guru.context';
import { useProductContext } from 'context/product/product.context';
import { useIsB2BCustomer } from 'hooks';
import { useEspnEligibilityRedirect } from 'pages/Espn/hooks/use-espn-eligibility-redirect.hook';
import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ContentUtil } from 'utils';
import { PageIds } from 'utils/guru';

import ESPNProposalEscapeModal from './ESPNProposalEscapeModal';
import { content } from './espn-proposal.content';
import { EscapeLink } from './espn-proposal.styles';
import { ProposalInfo } from './types/proposal-info';
import { getProposalsForType } from './utils/getProposalsForType';
import { getTypeFromId } from './utils/getTypeFromId';

const contentUtil = new ContentUtil({ content, componentName: 'ESPNProposal' });

export const ESPNProposal = () => {
  const [showEscapeModal, setShowEscapeModal] = React.useState(false);
  const [proposals, setProposals] = React.useState<ProposalInfo[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const { proposalId } = useParams<{ proposalId: string }>();
  const navigate = useNavigate();
  const isSoho = useIsB2BCustomer();

  const type = useMemo(() => getTypeFromId(proposalId), [proposalId]);

  if (!type) {
    return <PageNotFound />;
  }
  const { getTreatments } = useGuruContext();
  const { hasDashboardProductsLoaded, hasDTV, isLoading, hasError, products } = useProductContext();
  const { shouldRedirectToOverview, shouldShowGuardLoader } = useEspnEligibilityRedirect({
    contractEndDate: products?.groupLevelProduct?.contractEndDate,
    hasDashboardProductsLoaded,
    hasDTV,
    hasError,
    isLoading,
    navigate,
  });

  useEffect(() => {
    if (!hasDashboardProductsLoaded || isLoading || shouldRedirectToOverview) {
      return;
    }

    const fetchTreatments = async () => {
      const treatments = await getTreatments(PageIds.espn);
      setProposals(
        getProposalsForType(
          type,
          treatments.map((treatment) => treatment.id),
          isSoho
        )
      );
      setLoading(false);
    };

    fetchTreatments();
  }, [getTreatments, type, hasDashboardProductsLoaded, isLoading, shouldRedirectToOverview, isSoho]);

  return (
    <ChildPage>
      <Wrapper>
        {(loading || shouldShowGuardLoader) && <Loader />}
        <Row data-testid="espn-proposal-content">
          <Col>
            <Row>
              <h2>{contentUtil.translateToString(`page.${type}.title`)}</h2>
              <p className="m-0">{contentUtil.translateToString(`page.${type}.text`)}</p>
            </Row>
            <Row noMargin data-testid="espn-proposal-cards">
              {proposals.map((proposal) => (
                <Col
                  l={12 / proposals.length}
                  s={4}
                  key={`${proposal.title}-${proposal.ctaLink}`}
                  data-testid="espn-proposal-card">
                  <Card>
                    <CardHeader>
                      <CardImage src={proposal.image} alt={proposal.title} />
                    </CardHeader>
                    <CardContent className="gap-md">
                      <CardTitle className="font-bold">{proposal.title}</CardTitle>
                      <p className="m-0">{proposal.text}</p>
                    </CardContent>
                    <CardFooter>
                      <VZButton className="w-full" href={proposal.ctaLink} testid="espn-proposal-cta">
                        {proposal.cta}
                      </VZButton>
                    </CardFooter>
                  </Card>
                </Col>
              ))}
            </Row>
            <Row>
              <Col>
                <span>
                  {contentUtil.translateToString('escape.text')}{' '}
                  <EscapeLink variant="text-only" className="underline" onClick={() => setShowEscapeModal(true)}>
                    {contentUtil.translateToString('escape.linkText')}
                  </EscapeLink>
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
      </Wrapper>

      {showEscapeModal && <ESPNProposalEscapeModal onClose={() => setShowEscapeModal(false)} />}
    </ChildPage>
  );
};

export default ESPNProposal;
